import { useEffect, useRef, useState } from "hono/jsx";
import { GeoPuzzleMap } from "./GeoPuzzleMap.tsx";
import { generateFarmName } from "@/features/rooms/lib/nameGenerator.ts";

import { GameMode, Player, RoomState, WSMessage } from "../server/types.ts";

import { GeoQuitModal } from "./GeoQuitModal.tsx";
import { GeoPlayerMenu } from "./GeoPlayerMenu.tsx";
import { GeoViewNotFound } from "./views/GeoViewNotFound.tsx";
import { GeoViewLocked } from "./views/GeoViewLocked.tsx";
import { GeoViewBanned } from "./views/GeoViewBanned.tsx";
import { GeoSidebar } from "./GeoSidebar.tsx";

export const GeoRoom = (
  { roomId, user }: {
    roomId: string;
    user?: {
      id: string;
      name?: string;
      reservedRooms?: any[];
      isAnonymous?: boolean;
    };
  },
) => {
  const [connected, setConnected] = useState(false);
  const [roomState, setRoomState] = useState<RoomState | null>(null);
  const [showCode, setShowCode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showQuitDialog, setShowQuitDialog] = useState(false);
  const [showScoreScreen, setShowScoreScreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [updateReservation, setUpdateReservation] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<
    {
      isOpen: boolean;
      title: string;
      message: string;
      showBanOption?: boolean;
      showUserBanOption?: boolean;
      showGlobalOption?: boolean;
      onConfirm: (data?: any) => void;
    }
  >({
    isOpen: false,
    title: "",
    message: "",
    showBanOption: false,
    showUserBanOption: false,
    showGlobalOption: false,
    onConfirm: () => {},
  });

  // Host settings
  const [selectedMode, setSelectedMode] = useState<GameMode>("QUIZ");
  const [selectedRegions, setSelectedRegions] = useState<string[]>(["World"]);
  const [countryLimit, setCountryLimit] = useState<number | undefined>(
    undefined,
  );
  const [incorrectPenalty, setIncorrectPenalty] = useState<number>(0);
  const [incorrectLimit, setIncorrectLimit] = useState<number | undefined>(
    undefined,
  );
  const [showAnswer, setShowAnswer] = useState<boolean>(false);
  const [hardMode, setHardMode] = useState<boolean>(false);
  const [useHighRes, setUseHighRes] = useState<boolean>(false);
  const [password, setPassword] = useState<string>(() => {
    if (typeof globalThis.window !== "undefined") {
      const urlParams = new URLSearchParams(globalThis.location.search);
      const urlPw = urlParams.get("password");
      if (urlPw) return urlPw;
      return localStorage.getItem(`geopuzzle_pw_${roomId}`) || "";
    }
    return "";
  });
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);
  const [currentWrongGuesses, setCurrentWrongGuesses] = useState<
    Record<string, string>
  >({});
  const [roomNotFound, setRoomNotFound] = useState<
    boolean | { found: false; isReserved?: boolean; isOwner?: boolean }
  >(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [inputPassword, setInputPassword] = useState("");
  const [settingsPassword, setSettingsPassword] = useState("");
  const [contextMenu, setContextMenu] = useState<
    { x: number; y: number; playerId: string } | null
  >(null);
  const [feedbackActive, setFeedbackActive] = useState<
    { targetIso2: string; targetName: string } | null
  >(null);
  const [retryCount, setRetryCount] = useState(0);
  const [banMessage, setBanMessage] = useState<string | null>(null);

  // Close context menu on global click (handled in GeoPlayerMenu if mounted, or main click handler)
  // We keep this to reset state if menu is unmounted or clicked outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    globalThis.window?.addEventListener("click", handleClick);
    return () => globalThis.window?.removeEventListener("click", handleClick);
  }, []);

  // Track current player ID and Name
  const [myPlayerId, setMyPlayerId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;

    // 1. Authenticated User ALWAYS wins - ignore stale storage
    if (user?.id && !user.isAnonymous) return user.id;

    // 2. Room-specific storage (for guests/reconnection)
    const stored = localStorage.getItem(`geopuzzle_player_${roomId}`);
    if (stored) return stored;

    // 3. Global legacy storage
    const globalStored = localStorage.getItem("geopuzzle_player_id");
    if (globalStored) {
      localStorage.setItem(`geopuzzle_player_${roomId}`, globalStored);
      return globalStored;
    }

    // 4. Fallback for new guests
    const newId = crypto.randomUUID();
    localStorage.setItem(`geopuzzle_player_${roomId}`, newId);
    return newId;
  });
  const [myName, setMyName] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    const stored = localStorage.getItem("geopuzzle_custom_name");
    if (stored) return stored;
    const newName = generateFarmName();
    localStorage.setItem("geopuzzle_custom_name", newName);
    return newName;
  });

  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState("");

  const saveName = (newName: string) => {
    const trimmed = newName.trim().slice(0, 20);
    if (!trimmed) return;
    setMyName(trimmed);
    localStorage.setItem("geopuzzle_custom_name", trimmed);
    wsRef.current?.send(
      JSON.stringify({
        type: "UPDATE_PLAYER_PROFILE",
        params: { name: trimmed },
      }),
    );
    setIsEditingName(false);
  };

  const myPlayer = roomState?.players.find((p) => p.id === myPlayerId);

  const wsRef = useRef<WebSocket | null>(null);

  // Sync input password when error occurs (Auto-fill)
  useEffect(() => {
    if (passwordError && password) {
      setInputPassword(password);
    }
  }, [passwordError, password]);

  // WebSocket Effect
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!roomId) return;

    // Cleanup previous
    if (wsRef.current) {
      // console.log("[GeoRoom] Cleaning up previous socket", wsRef.current.readyState);
      wsRef.current.close();
    }

    const protocol = globalThis.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl =
      `${protocol}//${globalThis.location.host}/ws/geo?room=${roomId}&playerId=${
        myPlayerId || ""
      }&password=${encodeURIComponent(password)}&name=${
        encodeURIComponent(myName)
      }`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      setBanMessage(null);
    };

    ws.onclose = (e) => {
      setConnected(false);
      if (e.code === 4003 || e.code === 1008) {
        setBanMessage(e.reason || "You have been banned from this room.");
      } else {
        // Try to reconnect if not banned
        setTimeout(() => {
          setRetryCount((c) => c + 1);
        }, 2000); // 2s retry delay
      }
    };

    ws.onerror = (e) => {
      console.error("[GeoRoom] WS Error", e);
      // Close logic above handles retry
    };

    ws.onmessage = (event) => {
      try {
        const data: WSMessage = JSON.parse(event.data);
        if (data.type === "BANNED") {
          setBanMessage(
            (data as any).reason || "You have been banned from this room.",
          );
        } else if (data.type === "ROOM_STATE") {
          setRoomState(data as unknown as RoomState);
          setRoomNotFound(false);
          setPasswordError(null);

          // Sync settings password if we are host
          if (data.settings?.password) {
            setSettingsPassword(data.settings.password);
          }

          // Hide score screen when returning to lobby
          if (data.state === "LOBBY" && showScoreScreen) {
            setShowScoreScreen(false);
          }

          // Show score screen when game finishes
          if (
            data.state === "FINISHED" && !showScoreScreen &&
            roomState?.state !== "FINISHED"
          ) {
            setShowScoreScreen(true);
          }

          // SERVER-AUTHORITATIVE IDENTITY
          // The server tells us EXACTLY who we are in every broadcast.
          if ((data as any).yourId && (data as any).yourId !== myPlayerId) {
            console.log(
              `[GeoRoom] Identity Synced by Server: ${myPlayerId} -> ${
                (data as any).yourId
              }`,
            );
            setMyPlayerId((data as any).yourId);
            localStorage.setItem(
              `geopuzzle_player_${roomId}`,
              (data as any).yourId,
            );
          }

          const currentMeId = (data as any).yourId || myPlayerId;

          // Update local host settings if we are host
          const me = data.players?.find((p: Player) => p.id === currentMeId);
          if (me?.isHost) {
            if (data.mode) setSelectedMode(data.mode);
            if (data.settings?.regions) {
              setSelectedRegions(data.settings.regions);
            }
            if (data.settings?.limit !== undefined) {
              setCountryLimit(data.settings.limit);
            }
            if (data.settings?.incorrectPenalty !== undefined) {
              setIncorrectPenalty(data.settings.incorrectPenalty);
            }
            if (data.settings?.incorrectLimit !== undefined) {
              setIncorrectLimit(data.settings.incorrectLimit);
            }
            if (data.settings?.showAnswer !== undefined) {
              setShowAnswer(data.settings.showAnswer);
            }
            if (data.settings?.hardMode !== undefined) {
              setHardMode(data.settings.hardMode);
            }
            if (data.settings?.useHighRes !== undefined) {
              setUseHighRes(data.settings.useHighRes);
            }
          }
        } else if (data.type === "GUESS_RESULT") {
          if (data.correct) {
            setCurrentWrongGuesses({});
            setFeedbackActive(null);
          } else if (data.targetIso2 && data.guessedIso2) {
            // ignore further incorrect guesses if we already are in feedback mode
            if (feedbackActive) return;

            const iso = data.guessedIso2;
            const name = data.guessedName || data.guessedIso2;
            setCurrentWrongGuesses((prev) => {
              const updated = { ...prev, [iso]: name };
              return updated;
            });

            if (data.showFeedback) {
              console.log("[Feedback Active]", {
                targetIso2: data.targetIso2,
                targetName: data.targetName,
              });
              setFeedbackActive({
                targetIso2: data.targetIso2,
                targetName: data.targetName || data.targetIso2,
              });
            }
          }
        } else if (data.type === "ROOM_NOT_FOUND") {
          setRoomNotFound({
            found: false,
            isReserved: data.isReserved,
            isOwner: data.isOwner,
          });
        } else if (data.type === "ERROR") {
          if (data.message === "Incorrect password") {
            setPasswordError("Incorrect password");
            localStorage.removeItem(`geopuzzle_pw_${roomId}`);
          }
        }
      } catch (e) {
        console.error("Failed to parse WS message", e);
      }
    };

    return () => {
      // Prevent reconnect logic on cleanup/unmount
      ws.onclose = null;
      ws.close();
    };
  }, [roomId, retryCount, password]);

  // Restore feedback state from server (for reconnects)
  useEffect(() => {
    if (!myPlayer || !roomState) return;

    const p = myPlayer as any;
    const limit = roomState.settings?.incorrectLimit || 0;
    const effectiveLimit = (roomState.settings?.showAnswer && limit === 0)
      ? 1
      : limit;

    // Robust check: Server says limit reached OR client calcs it
    const isLimitReached = p.limitReached ||
      (effectiveLimit > 0 && (p.wrongGuesses || 0) >= effectiveLimit);

    if (isLimitReached && p.currentTarget) {
      console.log("[GeoRoom] Restoring feedback state", {
        serverFlag: p.limitReached,
        calcFlag: isLimitReached,
        guesses: p.wrongGuesses,
      });

      // Ensure we have the latest wrong guesses
      setCurrentWrongGuesses(p.currentWrongGuesses || {});

      // Activate feedback
      setFeedbackActive({
        targetIso2: p.currentTarget,
        targetName: p.currentTargetName || p.currentTarget,
      });
    }
  }, [
    myPlayer?.limitReached,
    myPlayer?.wrongGuesses,
    myPlayer?.currentTarget,
    roomState?.settings?.incorrectLimit,
  ]);

  useEffect(() => {
    // Only reset if we truly moved to a new target (or cleared target)
    if (
      feedbackActive && myPlayer?.currentTarget !== feedbackActive.targetIso2
    ) {
      setCurrentWrongGuesses({});
      setFeedbackActive(null);
    }
  }, [myPlayer?.currentTarget]);

  const stopGame = () => {
    wsRef.current?.send(JSON.stringify({ type: "STOP_GAME" }));
  };

  const placePiece = (countryId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "PIECE_PLACED",
        countryId,
      }));
    }
  };

  if (roomNotFound) {
    const info = typeof roomNotFound === "object"
      ? roomNotFound
      : { found: false, isReserved: false, isOwner: false };
    // Block creation if anonymous
    const canCreate = user && !user.isAnonymous;

    return (
      <GeoViewNotFound
        roomId={roomId}
        info={info as any}
        canCreate={!!canCreate}
        onCreate={async () => {
          try {
            const res = await fetch(`/api/geo/rooms`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                customId: roomId,
                creatorId: myPlayerId,
              }),
            });
            if (res.ok) {
              setRoomNotFound(false);
              setRetryCount((prev) => prev + 1);
            }
          } catch (err) {
            console.error("Failed to create room", err);
          }
        }}
      />
    );
  }

  if (passwordError) {
    return (
      <GeoViewLocked
        roomId={roomId}
        inputPassword={inputPassword}
        setInputPassword={setInputPassword}
        onSubmit={() => {
          const pw = inputPassword.trim();
          if (pw) {
            setPassword(pw);
            localStorage.setItem(`geopuzzle_pw_${roomId}`, pw);
            setPasswordError(null);
            setRetryCount((c) => c + 1);
          }
        }}
      />
    );
  }

  if (banMessage) {
    return <GeoViewBanned message={banMessage} />;
  }

  if (!connected) {
    return (
      <div class="p-10 text-center animate-pulse">Connecting to Room...</div>
    );
  }
  if (!roomState) {
    return <div class="p-10 text-center">Loading Room State...</div>;
  }

  return (
    <div class="flex h-screen bg-base-100">
      {/* Sidebar */}
      <GeoSidebar
        sidebarCollapsed={sidebarCollapsed}
        showCode={showCode}
        setShowCode={setShowCode}
        roomId={roomId}
        showQuitDialog={showQuitDialog}
        setShowQuitDialog={setShowQuitDialog}
        roomState={roomState as any}
        myPlayerId={myPlayerId}
        myPlayer={myPlayer as any}
        stopGame={stopGame}
        contextMenu={contextMenu}
        setContextMenu={setContextMenu}
        isEditingName={isEditingName}
        setIsEditingName={setIsEditingName}
        tempName={tempName}
        setTempName={setTempName}
        saveName={saveName}
        myName={myName}
        setShowSettings={setShowSettings}
      />

      {/* Main Map Area */}
      <div key="view-map" class="flex-1 relative bg-blue-50">
        {/* Map Component */}
        <GeoPuzzleMap
          showMap={!!roomState}
          hardMode={!!roomState?.settings?.hardMode}
          useHighRes={!!roomState?.settings?.useHighRes}
          onPlacePiece={placePiece}
          feedbackActive={feedbackActive}
          currentWrongGuesses={currentWrongGuesses}
          setMapReady={setMapReady}
          mapReady={mapReady}
        />

        {/* Overlay if waiting or finished */}
        {(roomState.state === "LOBBY" ||
          (roomState.state === "FINISHED" && !showScoreScreen)) && (
          <div class="absolute inset-0 bg-base-100/50 backdrop-blur-sm z-10 flex flex-col items-center justify-center p-8 space-y-8">
            {roomState.state === "LOBBY"
              ? (
                <>
                  {showCode && (
                    <div class="p-4 bg-white rounded-2xl shadow-xl transform hover:scale-105 transition-transform duration-300 pointer-events-auto flex flex-col items-center">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${
                          encodeURIComponent(
                            roomState.settings.password && myPlayer?.isHost
                              ? `${globalThis.location.origin}/geo/${roomId}?password=${roomState.settings.password}`
                              : globalThis.location.href,
                          )
                        }`}
                        alt="Room QR Code"
                        class="w-64 h-64 md:w-96 md:h-96 object-contain"
                      />
                      <div class="text-sm text-center font-mono mt-2 font-bold opacity-60">
                        SCAN TO JOIN
                      </div>
                      <button
                        type="button"
                        class={`btn btn-xs mt-2 ${
                          copyFeedback ? "btn-success" : "btn-ghost"
                        }`}
                        onClick={() => {
                          const url =
                            roomState.settings.password && myPlayer?.isHost
                              ? `${globalThis.location.origin}/geo/${roomId}?password=${roomState.settings.password}`
                              : globalThis.location.href;
                          navigator.clipboard.writeText(url);
                          setCopyFeedback(true);
                          setTimeout(() => setCopyFeedback(false), 2000);
                        }}
                      >
                        {copyFeedback ? "COPIED!" : "COPY LINK"}
                      </button>
                    </div>
                  )}

                  <div class="text-center space-y-2 pointer-events-auto">
                    <h2 class="text-4xl md:text-6xl font-black text-base-content/20 uppercase tracking-tighter select-none">
                      {roomState.mode === "QUIZ" ? "Quiz Mode" : "Puzzle Mode"}
                    </h2>
                    {myPlayer?.isHost
                      ? (
                        <button
                          class="btn btn-primary btn-lg shadow-lg font-black uppercase text-xl"
                          onClick={() =>
                            wsRef.current?.send(
                              JSON.stringify({ type: "START_GAME" }),
                            )}
                        >
                          Start Game
                        </button>
                      )
                      : (
                        <div class="inline-block px-6 py-3 bg-base-100 rounded-full shadow-sm border border-base-200 animate-pulse">
                          <span class="font-bold opacity-60">
                            Waiting for host to start...
                          </span>
                        </div>
                      )}
                  </div>
                </>
              )
              : (
                <div class="bg-base-100 p-8 rounded-2xl shadow-xl flex flex-col items-center space-y-4 pointer-events-auto border border-base-200">
                  <h2 class="text-2xl font-black uppercase text-center">
                    Game Over
                  </h2>
                  <p class="text-sm opacity-60">
                    Waiting for host to start the next round...
                  </p>
                  <div class="flex gap-2">
                    <button
                      class="btn btn-outline"
                      onClick={() => setShowScoreScreen(true)}
                    >
                      View Scores
                    </button>
                    {myPlayer?.isHost && (
                      <button
                        class="btn btn-primary"
                        onClick={() =>
                          wsRef.current?.send(
                            JSON.stringify({ type: "RESET_GAME" }),
                          )}
                      >
                        New Round
                      </button>
                    )}
                  </div>
                </div>
              )}
          </div>
        )}
        {/* Current Player Target Overlay */}

        {roomState.state === "PLAYING" && myPlayer?.currentTargetName && (
          <div class="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 pointer-events-none flex flex-col items-center gap-2">
            <div class="bg-primary text-primary-content px-6 py-3 rounded-full shadow-2xl font-black text-2xl animate-pulse">
              Find: {myPlayer.currentTargetName}
            </div>

            <div class="bg-base-100/90 backdrop-blur px-3 py-1.5 rounded-full shadow-lg border border-base-200 flex items-center gap-1.5">
              {(() => {
                const limit = roomState.settings.incorrectLimit || 0;
                const used = myPlayer.wrongGuesses || 0;
                const effectiveLimit =
                  (roomState.settings.showAnswer && limit === 0) ? 1 : limit;

                if (effectiveLimit === 0) {
                  return (
                    <span class="text-xs font-bold uppercase text-base-content/70 tracking-wider">
                      Mistakes: {used}
                    </span>
                  );
                }

                return (
                  <div class="flex gap-1">
                    {Array.from({ length: effectiveLimit }).map((_, i) => (
                      <div
                        key={i}
                        class={`w-2.5 h-2.5 rounded-full border-2 border-error transition-all duration-300 ${
                          i < (effectiveLimit - used)
                            ? "bg-error scale-100"
                            : "bg-transparent scale-90 opacity-50"
                        }`}
                      />
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Sidebar Toggle Button */}
        <button
          type="button"
          class="absolute top-4 left-4 z-20 btn btn-circle btn-sm btn-ghost bg-base-200/80 backdrop-blur"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          {sidebarCollapsed
            ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            )
            : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
            )}
        </button>
      </div>

      {/* Quit Confirmation Dialog */}
      <GeoQuitModal
        isOpen={showQuitDialog}
        onClose={() => setShowQuitDialog(false)}
        onQuit={() => {
          wsRef.current?.send(JSON.stringify({ type: "LEAVE_ROOM" }));
          wsRef.current?.close();
          localStorage.removeItem(`geopuzzle_player_${roomId}`);
          globalThis.location.href = "/geo";
        }}
      />

      {/* Context Menu */}
      {contextMenu && (
        <GeoPlayerMenu
          x={contextMenu.x}
          y={contextMenu.y}
          playerId={contextMenu.playerId}
          onClose={() => setContextMenu(null)}
          onKick={(id) =>
            wsRef.current?.send(
              JSON.stringify({ type: "KICK_PLAYER", playerId: id }),
            )}
          onBan={(id) =>
            setConfirmModal({
              isOpen: true,
              title: "Ban User",
              message: "Are you sure you want to ban this user?",
              showUserBanOption: true,
              onConfirm: (data) =>
                wsRef.current?.send(JSON.stringify({
                  type: "BAN_PLAYER",
                  playerId: id,
                  banIP: true,
                  banUser: data?.banUser,
                })),
            })}
        />
      )}

      {/* Score Screen Modal */}
      {showScoreScreen && roomState?.state === "FINISHED" && (
        <dialog open class="modal modal-open">
          <div class="modal-box max-w-2xl">
            <h3 class="font-bold text-3xl mb-6 text-center">Game Complete</h3>

            <div class="space-y-3 mb-6">
              {[...roomState.players]
                .sort((a, b) =>
                  b.score - a.score
                )
                .map((p, index) => (
                  <div
                    key={p.id}
                    class="flex flex-col gap-2 p-2 rounded-lg bg-base-200"
                  >
                    <div
                      class={`flex items-center gap-4 p-2 cursor-pointer hover:bg-base-300 rounded-lg transition-colors ${
                        index === 0 ? "bg-warning/20 border border-warning" : ""
                      }`}
                      onClick={() =>
                        setExpandedPlayerId(
                          expandedPlayerId === p.id ? null : p.id,
                        )}
                    >
                      <div class="text-xl font-black w-6">#{index + 1}</div>
                      <div
                        class="w-4 h-4 rounded-full"
                        style={{ backgroundColor: p.color }}
                      >
                      </div>
                      <div class="flex-1">
                        <div class="font-bold text-sm">{p.name}</div>
                        <div class="text-[10px] opacity-60 uppercase font-black">
                          {p.progress} / {p.totalTargets || 0} countries
                        </div>
                      </div>
                      <div class="text-xl font-black">{p.score}</div>
                      <div class="opacity-40">
                        {expandedPlayerId === p.id
                          ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              class="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M5 15l7-7 7 7"
                              />
                            </svg>
                          )
                          : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              class="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          )}
                      </div>
                    </div>

                    {expandedPlayerId === p.id && p.targetHistory && (
                      <div class="p-2 bg-base-100 rounded border border-base-300 overflow-hidden text-xs">
                        <table class="table table-compact w-full">
                          <thead>
                            <tr class="text-[10px] uppercase opacity-60">
                              <th>Country</th>
                              <th class="text-right">Time</th>
                              <th class="text-right">Wrong</th>
                            </tr>
                          </thead>
                          <tbody>
                            {p.targetHistory.map((h, i) => (
                              <tr key={i} class="hover">
                                <td class="font-bold">
                                  {h.targetName || h.targetId}
                                </td>
                                <td class="text-right">
                                  {(h.time / 1000).toFixed(1)}s
                                </td>
                                <td class="text-right">
                                  <span
                                    class={h.wrongCount > 0
                                      ? "text-error font-bold"
                                      : "opacity-40"}
                                  >
                                    {h.wrongCount}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
            </div>

            <div class="modal-action">
              {myPlayer?.isHost
                ? (
                  <button
                    type="button"
                    class="btn btn-primary w-full font-black uppercase"
                    onClick={() => {
                      setShowScoreScreen(false);
                      wsRef.current?.send(
                        JSON.stringify({ type: "RESET_GAME" }),
                      );
                    }}
                  >
                    New Round
                  </button>
                )
                : (
                  <div class="text-center w-full opacity-60">
                    Waiting for host to start new round...
                  </div>
                )}
            </div>
          </div>
          <div class="modal-backdrop" onClick={() => setShowScoreScreen(false)}>
          </div>
        </dialog>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <dialog open class="modal modal-open z-9999">
          <div class="modal-box max-w-lg">
            <div class="flex justify-between items-center mb-6">
              <h3 class="font-bold text-2xl uppercase tracking-tighter">
                Game Settings
              </h3>
              <button
                type="button"
                class="btn btn-sm btn-ghost btn-circle"
                onClick={() => setShowSettings(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div class="space-y-4">
              {/* ... (lines 800-989) ... */}
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-bold">Game Mode</span>
                </label>
                <select
                  class="select select-bordered w-full"
                  value={selectedMode}
                  onChange={(e) => {
                    const mode = (e.target as HTMLSelectElement).value as
                      | "QUIZ"
                      | "PUZZLE";
                    setSelectedMode(mode);
                    wsRef.current?.send(
                      JSON.stringify({ type: "UPDATE_SETTINGS", mode }),
                    );
                  }}
                >
                  <option value="QUIZ">Quiz Mode</option>
                  <option value="PUZZLE">Puzzle Mode</option>
                </select>
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text font-bold">Region Filter</span>
                </label>
                <select
                  class="select select-bordered w-full"
                  value={selectedRegions[0] || "World"}
                  onChange={(e) => {
                    const region = (e.target as HTMLSelectElement).value;
                    const regions = region === "World" ? ["World"] : [region];
                    setSelectedRegions(regions);
                    wsRef.current?.send(
                      JSON.stringify({ type: "UPDATE_SETTINGS", regions }),
                    );
                  }}
                >
                  <option value="World">World</option>
                  <option value="Africa">Africa</option>
                  <option value="Asia">Asia</option>
                  <option value="Europe">Europe</option>
                  <option value="North America">North America</option>
                  <option value="South America">South America</option>
                  <option value="Oceania">Oceania</option>
                </select>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-bold">Country Limit</span>
                  </label>
                  <input
                    type="number"
                    placeholder="No limit"
                    class="input input-bordered w-full"
                    min="1"
                    value={countryLimit || ""}
                    onChange={(e) => {
                      const val = (e.target as HTMLInputElement).value;
                      const limit = val ? parseInt(val) : undefined;
                      setCountryLimit(limit);
                      wsRef.current?.send(
                        JSON.stringify({ type: "UPDATE_SETTINGS", limit }),
                      );
                    }}
                  />
                </div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-bold">Wrong Guess Limit</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Unlimited"
                    class="input input-bordered w-full"
                    min="1"
                    value={incorrectLimit || ""}
                    onChange={(e) => {
                      const val = (e.target as HTMLInputElement).value;
                      const limit = val ? parseInt(val) : undefined;
                      setIncorrectLimit(limit);
                      wsRef.current?.send(
                        JSON.stringify({
                          type: "UPDATE_SETTINGS",
                          incorrectLimit: limit,
                        }),
                      );
                    }}
                  />
                </div>
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text font-bold">Wrong Guess Penalty</span>
                </label>
                <input
                  type="number"
                  class="input input-bordered w-full"
                  min="0"
                  value={incorrectPenalty}
                  onChange={(e) => {
                    const penalty =
                      parseInt((e.target as HTMLInputElement).value) || 0;
                    setIncorrectPenalty(penalty);
                    wsRef.current?.send(
                      JSON.stringify({
                        type: "UPDATE_SETTINGS",
                        incorrectPenalty: penalty,
                      }),
                    );
                  }}
                />
              </div>

              <div class="grid grid-cols-2 gap-4 pt-2">
                <div class="form-control">
                  <label class="label cursor-pointer justify-start gap-3">
                    <input
                      type="checkbox"
                      class="checkbox checkbox-primary"
                      checked={showAnswer}
                      onChange={(e) => {
                        const checked = (e.target as HTMLInputElement).checked;
                        setShowAnswer(checked);
                        wsRef.current?.send(
                          JSON.stringify({
                            type: "UPDATE_SETTINGS",
                            showAnswer: checked,
                          }),
                        );
                      }}
                    />
                    <span class="label-text font-bold">Show Answer</span>
                  </label>
                </div>
                <div class="form-control">
                  <label class="label cursor-pointer justify-start gap-3">
                    <input
                      type="checkbox"
                      class="checkbox checkbox-error"
                      checked={hardMode}
                      onChange={(e) => {
                        const checked = (e.target as HTMLInputElement).checked;
                        setHardMode(checked);
                        wsRef.current?.send(
                          JSON.stringify({
                            type: "UPDATE_SETTINGS",
                            hardMode: checked,
                          }),
                        );
                      }}
                    />
                    <span class="label-text font-bold">Hard Mode</span>
                  </label>
                </div>
              </div>

              <div class="form-control">
                <label class="label cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-secondary"
                    checked={useHighRes}
                    onChange={(e) => {
                      const checked = (e.target as HTMLInputElement).checked;
                      setUseHighRes(checked);
                      wsRef.current?.send(
                        JSON.stringify({
                          type: "UPDATE_SETTINGS",
                          useHighRes: checked,
                        }),
                      );
                    }}
                  />
                  <div class="flex flex-col">
                    <span class="label-text font-bold">Higher Res Shapes</span>
                    <span class="text-[10px] opacity-70">
                      Slower load, better detail
                    </span>
                  </div>
                </label>
              </div>

              <div class="divider text-[10px] font-black opacity-30 uppercase tracking-widest">
                Visibility & Security
              </div>

              <div class="form-control">
                <label class="label cursor-pointer justify-start gap-4">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-primary"
                    checked={roomState.settings.isPublic}
                    onChange={(e) =>
                      wsRef.current?.send(JSON.stringify({
                        type: "UPDATE_SETTINGS",
                        isPublic: (e.target as HTMLInputElement).checked,
                      }))}
                    disabled={user?.isAnonymous}
                  />
                  <span
                    class={`label-text font-bold ${
                      user?.isAnonymous ? "opacity-50" : ""
                    }`}
                  >
                    Public Game {user?.isAnonymous && "(Login required)"}
                  </span>
                </label>
                <div class="text-[10px] opacity-40 ml-8 mb-4">
                  Public rooms appear in the lobby and search.
                </div>
              </div>

              {(roomState.bannedIps?.length || roomState.kickedUserIds?.length)
                ? (
                  <>
                    <div class="divider text-[10px] font-black opacity-30 uppercase tracking-widest text-error">
                      Banned Players
                    </div>
                    <div class="bg-base-200 rounded-lg p-2 max-h-40 overflow-y-auto space-y-2">
                      {roomState.kickedUserIds &&
                        roomState.kickedUserIds.length > 0 && (
                        <div>
                          <div class="text-[10px] font-bold opacity-60 px-2 mb-1">
                            Kicked (Session)
                          </div>
                          {roomState.kickedUserIds.map((uid) => (
                            <div class="flex justify-between items-center bg-base-100 p-2 rounded text-xs">
                              <span>User {uid.slice(0, 8)}...</span>
                              <button
                                type="button"
                                class="btn btn-xs btn-error btn-outline"
                                onClick={() =>
                                  wsRef.current?.send(
                                    JSON.stringify({
                                      type: "UNBAN_PLAYER",
                                      params: { targetId: uid },
                                    }),
                                  )}
                              >
                                Unban
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {roomState.bannedUsers &&
                        roomState.bannedUsers.length > 0 && (
                        <div>
                          <div class="text-[10px] font-bold opacity-60 px-2 mt-2 mb-1">
                            Banned Users (Accounts)
                          </div>
                          {roomState.bannedUsers.map((ban) => (
                            <div class="flex justify-between items-center bg-base-100 p-2 rounded text-xs mb-1">
                              <div class="flex flex-col">
                                <span class="font-bold">{ban.username}</span>
                                <span class="opacity-50 text-[10px]">
                                  {new Date(ban.timestamp).toLocaleTimeString()}
                                </span>
                              </div>
                              <div class="flex gap-1">
                                {roomState.ownerId === myPlayerId && (
                                  <button
                                    type="button"
                                    class="btn btn-xs btn-error"
                                    title="Unban Globally"
                                    onClick={() =>
                                      wsRef.current?.send(
                                        JSON.stringify({
                                          type: "UNBAN_IP",
                                          params: {
                                            banId: ban.id,
                                            global: true,
                                          },
                                        }),
                                      )}
                                  >
                                    Global Unban
                                  </button>
                                )}
                                <button
                                  type="button"
                                  class="btn btn-xs btn-warning btn-outline"
                                  onClick={() =>
                                    wsRef.current?.send(
                                      JSON.stringify({
                                        type: "UNBAN_IP",
                                        params: { banId: ban.id },
                                      }),
                                    )}
                                >
                                  Unban
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {roomState.bannedIps && roomState.bannedIps.length > 0 &&
                        (
                          <div>
                            <div class="text-[10px] font-bold opacity-60 px-2 mt-2 mb-1">
                              Banned (Recoverable IDs)
                            </div>
                            {roomState.bannedIps.map((ban) => (
                              <div class="flex justify-between items-center bg-base-100 p-2 rounded text-xs mb-1">
                                <div class="flex flex-col">
                                  <span class="font-bold">{ban.username}</span>
                                  <span class="opacity-50 text-[10px]">
                                    {new Date(ban.timestamp)
                                      .toLocaleTimeString()}
                                  </span>
                                </div>
                                <div class="flex gap-1">
                                  {roomState.ownerId === myPlayerId && (
                                    <button
                                      type="button"
                                      class="btn btn-xs btn-error"
                                      title="Unban Globally"
                                      onClick={() =>
                                        wsRef.current?.send(
                                          JSON.stringify({
                                            type: "UNBAN_IP",
                                            params: {
                                              banId: ban.id,
                                              global: true,
                                            },
                                          }),
                                        )}
                                    >
                                      Global Unban
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    class="btn btn-xs btn-warning btn-outline"
                                    onClick={() =>
                                      wsRef.current?.send(
                                        JSON.stringify({
                                          type: "UNBAN_IP",
                                          params: { banId: ban.id },
                                        }),
                                      )}
                                  >
                                    Unban
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </>
                )
                : null}

              <div class="form-control">
                <label class="label">
                  <span class="label-text font-bold">Room Password</span>
                </label>
                <input
                  type="text"
                  class="input input-bordered w-full font-mono"
                  value={settingsPassword}
                  placeholder={roomState.settings.hasPassword
                    ? "Change password..."
                    : "No password (leave empty)"}
                  onChange={(e) =>
                    setSettingsPassword((e.target as HTMLInputElement).value)}
                  onBlur={() => {
                    // Update the room password on server
                    wsRef.current?.send(JSON.stringify({
                      type: "UPDATE_SETTINGS",
                      password: settingsPassword,
                      updateReservation,
                    }));
                  }}
                />
                {(() => {
                  const isReservedByUser = (user?.reservedRooms || []).some((
                    r: any,
                  ) => r.id === roomId);

                  return roomState.ownerId === myPlayerId && isReservedByUser &&
                    (
                      <label class="label cursor-pointer justify-start gap-2 py-1">
                        <input
                          type="checkbox"
                          class="checkbox checkbox-xs checkbox-primary"
                          checked={updateReservation}
                          onChange={(e) => {
                            const checked =
                              (e.target as HTMLInputElement).checked;
                            setUpdateReservation(checked);
                            if (checked) {
                              wsRef.current?.send(JSON.stringify({
                                type: "UPDATE_SETTINGS",
                                password: password,
                                updateReservation: true,
                              }));
                            }
                          }}
                        />
                        <span class="label-text text-[10px] font-bold opacity-70">
                          Update my reservation password
                        </span>
                      </label>
                    );
                })()}
                <div class="text-[10px] opacity-40 mt-1">
                  Leave empty for open access.
                </div>
              </div>

              <div class="modal-action pt-4">
                <button
                  type="button"
                  class="btn btn-ghost w-full font-black uppercase"
                  onClick={() => setShowSettings(false)}
                >
                  Close Settings
                </button>
              </div>
            </div>
          </div>
          <div class="modal-backdrop" onClick={() => setShowSettings(false)}>
          </div>
        </dialog>
      )}
      {/* Context Menu */}
      {contextMenu && (
        <div
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
          }}
          class="bg-base-100 shadow-xl border border-base-300 rounded-lg overflow-hidden min-w-[140px] flex flex-col py-1 animate-in fade-in zoom-in duration-100"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Promote Option (Only for other players) */}
          {contextMenu.playerId !== myPlayerId && (
            <button
              type="button"
              class="px-4 py-2 hover:bg-base-200 text-sm font-bold flex items-center gap-2 text-warning w-full text-left"
              onClick={() => {
                const pId = contextMenu.playerId;
                const pName = roomState.players.find((p) =>
                  p.id === pId
                )?.name || "Player";
                setConfirmModal({
                  isOpen: true,
                  title: "Promote to Host?",
                  message:
                    `Are you sure you want to make ${pName} the Host? You will lose host privileges.`,
                  onConfirm: () => {
                    wsRef.current?.send(
                      JSON.stringify({
                        type: "PROMOTE_PLAYER",
                        params: { targetId: pId },
                      }),
                    );
                  },
                });
                setContextMenu(null);
              }}
            >
              Make Host
            </button>
          )}

          {/* Reclaim Host (Self Only, if Owner and not Host) */}
          {contextMenu.playerId === myPlayerId &&
            roomState.ownerId === myPlayerId && !roomState.players.find((p) =>
              p.id === myPlayerId
            )?.isHost && (
            <button
              type="button"
              class="px-4 py-2 hover:bg-base-200 text-sm font-bold flex items-center gap-2 text-warning w-full text-left"
              onClick={() => {
                setContextMenu(null);
                setConfirmModal({
                  isOpen: true,
                  title: "Reclaim Host?",
                  message: "Are you sure you want to reclaim Host status?",
                  onConfirm: () => {
                    wsRef.current?.send(
                      JSON.stringify({
                        type: "PROMOTE_PLAYER",
                        params: { targetId: myPlayerId },
                      }),
                    );
                  },
                });
              }}
            >
              Reclaim Host
            </button>
          )}

          {/* Kick Option (Only for other players) */}
          {contextMenu.playerId !== myPlayerId && (
            <button
              type="button"
              class="px-4 py-2 hover:bg-error/10 text-sm font-bold flex items-center gap-2 text-error w-full text-left"
              onClick={() => {
                const pId = contextMenu.playerId;
                const target = roomState.players.find((p) => p.id === pId);
                const pName = target?.name || "Player";
                const hasUserId = !!target?.userId;

                setConfirmModal({
                  isOpen: true,
                  title: "Kick Player?",
                  message:
                    `Are you sure you want to kick ${pName} from the room?`,
                  showBanOption: true,
                  showUserBanOption: hasUserId,
                  onConfirm: (data) => {
                    wsRef.current?.send(JSON.stringify({
                      type: "KICK_PLAYER",
                      params: {
                        targetId: pId,
                        banIp: data?.banIp,
                        banUser: data?.banUser,
                        global: data?.global,
                      },
                    }));
                  },
                });
                setContextMenu(null);
              }}
            >
              Kick Player
            </button>
          )}
        </div>
      )}
      {/* Confirmation Modal */}
      <dialog class={`modal z-9999 ${confirmModal.isOpen ? "modal-open" : ""}`}>
        <div class="modal-box">
          <h3 class="font-bold text-lg">{confirmModal.title}</h3>
          <p class="py-4 font-medium opacity-80">{confirmModal.message}</p>

          {(confirmModal.showBanOption || confirmModal.showUserBanOption) && (
            <div class="space-y-4 py-2 border-y border-base-300">
              {confirmModal.showBanOption && (
                <div class="form-control">
                  <label class="label cursor-pointer justify-start gap-4">
                    <input
                      type="checkbox"
                      id="banIpCheckbox"
                      class="checkbox checkbox-error"
                      onChange={(e) => {
                        const target = e.currentTarget as HTMLInputElement;
                        const userCb = document.getElementById(
                          "banUserCheckbox",
                        ) as HTMLInputElement;
                        // If IP is banned, User is also banned
                        if (target.checked && userCb) {
                          userCb.checked = true;
                          userCb.disabled = true;
                        } else if (userCb) {
                          userCb.disabled = false;
                        }
                      }}
                    />
                    <div class="flex flex-col">
                      <span class="label-text font-black text-error">
                        Ban IP Address
                      </span>
                      <span class="text-[10px] opacity-40 uppercase font-bold">
                        Hard block on their network
                      </span>
                    </div>
                  </label>
                </div>
              )}

              {confirmModal.showUserBanOption && (
                <div class="form-control">
                  <label class="label cursor-pointer justify-start gap-4">
                    <input
                      type="checkbox"
                      id="banUserCheckbox"
                      class="checkbox checkbox-error"
                    />
                    <div class="flex flex-col">
                      <span class="label-text font-black text-error">
                        Ban User Account
                      </span>
                      <span class="text-[10px] opacity-40 uppercase font-bold">
                        Prevents this account from joining
                      </span>
                    </div>
                  </label>
                </div>
              )}

              <div class="form-control pt-2 border-t border-base-300/50">
                <label class="label cursor-pointer justify-start gap-4">
                  <input
                    type="checkbox"
                    id="globalBanCheckbox"
                    class="checkbox checkbox-primary"
                  />
                  <div class="flex flex-col">
                    <span class="label-text font-black text-primary">
                      Global Ban
                    </span>
                    <span class="text-[10px] opacity-40 uppercase font-bold">
                      Apply this ban to ALL your rooms forever
                    </span>
                  </div>
                </label>
              </div>
            </div>
          )}

          <div class="modal-action">
            <button
              type="button"
              class="btn btn-ghost font-black uppercase"
              onClick={() =>
                setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-error font-black uppercase"
              onClick={() => {
                const banCb = document.getElementById(
                  "banIpCheckbox",
                ) as HTMLInputElement;
                const userCb = document.getElementById(
                  "banUserCheckbox",
                ) as HTMLInputElement;
                const globalCb = document.getElementById(
                  "globalBanCheckbox",
                ) as HTMLInputElement;
                confirmModal.onConfirm({
                  banIp: banCb?.checked,
                  banUser: userCb?.checked || banCb?.checked, // If IP is checked, User is implied
                  global: globalCb?.checked,
                });
                setConfirmModal((prev) => ({ ...prev, isOpen: false }));
              }}
            >
              Confirm Action
            </button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop">
          <button
            type="button"
            onClick={() =>
              setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
          >
            close
          </button>
        </form>
      </dialog>
    </div>
  );
};
